// services/websocketService.ts
import { io, Socket } from "socket.io-client";
import { notifySessionExpired } from "./session-events";
import { hostURL } from "utils/helper/states";

export interface ChatModel {
  date: Date;
  data: Data[];
}

export interface ChatModel2 {
  hasMore: boolean;
  nextBeforeDate?: Date;
  /** Tie-breaker for the history cursor (server sends it since v2.1). */
  nextBeforeId?: string | null;
  messages: Data[];
}

export interface Data {
  _id: string;
  userId: string;
  chatId: string;
  message?: string | null;
  image: any[];
  audio?: string | null;
  reply?: string | null;
  replyId?: string | null;
  syncId?: string | null;
  seenIds: any[];
  sentIds: any[];
  deleted: boolean;
  users: any[];
  date: Date;
  replyData?: Data | null;
  houseData?: ChatHouseDetails | null;
}

export interface ChatHouseDetails {
  image: string;
  _id: string;
  agentId?: string;
  title: string;
  businessName?: string;
}

export interface ChatListModel {
  _id: string;
  chatId: string;
  userNames: UserNames;
  lastMessage?: string;
  lastImage?: string;
  date: Date;
  seenIds: any[];
  userImages: UserImages;
  otherUserId?: string;
  isAudio: boolean;
  deleted: boolean;
}

/**
 * @interface UserNames {"userId": "username", "userId": "username"}
 */
export interface UserNames {
  [username: string]: string;
}

/**
 * @interface UserImages {"userId": "https://image.com", "userId": "https://image.com"}
 *
 * NT: image can be null
 */
export interface UserImages {
  [username: string]: string;
}

export type Unsubscribe = () => void;

/**
 * One Socket.IO connection per signed-in session (audit W03/W06).
 *
 * - `connect` closes any previous connection instead of leaking it.
 * - Every `listenTo*` returns an unsubscribe function; components must call
 *   it on cleanup so re-renders do not stack duplicate callbacks.
 * - Uses the v2 chat events the server supports (`delete-message2`).
 */
class WebSocketService {
  private socket: Socket | null = null;
  private token: string | null = null;
  private limit: number = 50;
  // Listeners live here, not only on the socket, so a component that
  // subscribes before `connect` (child effects run first) or across a
  // reconnect still receives events.
  private handlers = new Map<string, Set<(data: any) => void>>();
  // The server sends `chats` once per connection and again only on changes;
  // a chat list mounted later is replayed the latest copy.
  private lastChats: ChatListModel[] | null = null;
  // Requests made before the socket exists (e.g. joining the room on a page
  // opened directly) are sent once it does.
  private pending: [string, any][] = [];

  public connect(token: string) {
    if (this.socket && this.token === token) return;
    this.closeSocket();
    this.token = token;
    const socket = io(hostURL, {
      extraHeaders: { Authorization: token },
    });
    this.socket = socket;
    socket.on("unauthorized", () => {
      notifySessionExpired();
    });
    socket.on("connect_error", (error) => {
      if (error?.message === "Unauthorized") notifySessionExpired();
    });
    socket.on("chats", (data: any) => {
      if (data && data.success) this.lastChats = data.data;
    });
    this.handlers.forEach((set, event) => set.forEach((handler) => socket.on(event, handler)));
    const pending = this.pending;
    this.pending = [];
    pending.forEach(([event, data]) => socket.emit(event, data));
  }

  /** Sign-out: closes the socket and drops queued requests. */
  public disconnect() {
    this.pending = [];
    this.closeSocket();
  }

  private closeSocket() {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.token = null;
    this.lastChats = null;
  }

  /** Legacy v1 join (grouped `messages`). Prefer `joinRoom2`. */
  public joinRoom(chatId: string) {
    this.emit("joinRoom", chatId);
  }

  /**
   * Joins a chat and requests a page of history. For older pages pass the
   * `nextBeforeDate` and `nextBeforeId` cursor from the previous page.
   */
  public joinRoom2(chatId: string, nextBeforeDate?: Date | string | null, nextBeforeId?: string | null) {
    this.emit("joinRoom2", {
      chatId,
      beforeDate: nextBeforeDate ?? undefined,
      beforeId: nextBeforeId ?? undefined,
      limit: this.limit,
    });
  }

  public listenToMessages(callback: (message: ChatModel[]) => void): Unsubscribe {
    return this.addListener("messages", callback);
  }

  public listenToLastMessages(callback: (message: Data) => void): Unsubscribe {
    return this.addListener("message", callback);
  }

  public listenToMessages2(callback: (message: ChatModel2) => void): Unsubscribe {
    return this.addListener("messages2", callback);
  }

  public listenToDeletedMessages(callback: (messageId: string) => void): Unsubscribe {
    return this.addListener("deleted-ID", callback);
  }

  public listenToChatList(callback: (chats: ChatListModel[]) => void): Unsubscribe {
    if (this.lastChats) callback(this.lastChats);
    return this.addListener("chats", callback);
  }

  deleteMessage(chatId: string, messageId: string) {
    this.emit("delete-message2", { chatId, id: messageId });
  }

  private emit(event: string, data: any) {
    if (this.socket) this.socket.emit(event, data);
    else this.pending.push([event, data]);
  }

  private addListener<T>(event: string, callback: (data: T) => void): Unsubscribe {
    const handler = (data: any) => {
      if ((event === "message" || event === "deleted-ID") && data) {
        callback(data);
      } else if (data && data.success) {
        callback(data.data);
      }
    };
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers.get(event)!.add(handler);
    this.socket?.on(event, handler);
    return () => {
      this.handlers.get(event)?.delete(handler);
      this.socket?.off(event, handler);
    };
  }
}

export const websocketService = new WebSocketService();
